import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer';
import DashboardMetrics from '../../components/anchor/DashboardMetrics';
import InvoiceFilters from '../../components/anchor/InvoiceFilters';
import InvoiceList from '../../components/anchor/InvoiceList';
import InvoiceDetailModal from '../../components/anchor/InvoiceDetailModal';
import SupplierManagement from '../../components/anchor/SupplierManagement';
import AnalyticsCharts from '../../components/anchor/AnalyticsCharts';
import AnchorSettings from '../../components/anchor/AnchorSettings';
import anchorService from '../../services/anchorService';
import showToast from '../../utils/toast';
import '../../components/anchor/anchor-styles.css';

/**
 * Main Anchor Dashboard Page
 * Integrates all anchor-related components and manages state
 */
const AnchorDashboard = ({ addNotification }) => {
  const user = useSelector((state) => state.auth.user);
  const anchorId = user?.id; // Assuming user ID is anchor ID
  const location = useLocation();
  const navigate = useNavigate();

  // State Management
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardMetrics, setDashboardMetrics] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [analyticsData, setAnalyticsData] = useState({});
  const [anchorProfile, setAnchorProfile] = useState({});
  
  const [loading, setLoading] = useState({
    metrics: false,
    invoices: true,
    suppliers: false,
    analytics: false,
    profile: false
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    from: 0,
    to: 0
  });

  const [filters, setFilters] = useState({});
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoiceDetail, setSelectedInvoiceDetail] = useState(null);
  const [loadingInvoiceDetail, setLoadingInvoiceDetail] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [invoiceToReject, setInvoiceToReject] = useState(null);
  const [rejectionData, setRejectionData] = useState({
    reason: '',
    customReason: ''
  });
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [invoiceToAccept, setInvoiceToAccept] = useState(null);
  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [currentInvoiceForRemarks, setCurrentInvoiceForRemarks] = useState(null);
  const [remarksToDisplay, setRemarksToDisplay] = useState('');
  const [newRemarkText, setNewRemarkText] = useState('');
  const initialInvoicesLoadedRef = useRef(false);

  // Sync activeTab with URL query param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) {
      setActiveTab(tab);
    } else {
      // Default to invoices if no tab specified (since overview is hidden)
      // setActiveTab('invoices');
      navigate('/anchor/dashboard?tab=invoices', { replace: true });
    }
  }, [location.search, navigate]);

  // Load initial data
  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load data based on active tab
  useEffect(() => {
    switch (activeTab) {
      case 'overview':
        loadMetrics();
        if (!initialInvoicesLoadedRef.current) {
          initialInvoicesLoadedRef.current = true;
          loadInvoices();
        }
        break;
      case 'invoices':
        if (!initialInvoicesLoadedRef.current) {
          initialInvoicesLoadedRef.current = true;
          loadInvoices();
        }
        break;
      case 'suppliers':
        loadSuppliers();
        break;
      case 'analytics':
        loadAnalytics();
        break;
      case 'settings':
        loadProfile();
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadDashboardData = () => {
    loadMetrics();
  };

  const loadMetrics = async () => {
    setLoading({ ...loading, metrics: true });
    try {
      // const data = await anchorService.getDashboardMetrics(anchorId);
      // Mock data for now
      const data = {
        pendingInvoicesCount: 45,
        approvedThisMonth: 12500000,
        totalFinancedYTD: 125000000,
        availableCredit: 50000000,
        avgApprovalTime: 2.5,
        supplierCount: 28,
        overdueCount: 3,
        earlyPaymentSavings: 850000
      };
      setDashboardMetrics(data);
    } catch (error) {
      console.error('Error loading metrics:', error);
      showToast('Failed to load dashboard metrics', 'error');
    } finally {
      setLoading({ ...loading, metrics: false });
    }
  };

  const loadInvoices = async (page = pagination.currentPage, filterParams = filters) => {
    setLoading(prev => ({ ...prev, invoices: true }));
    try {
      const response = await anchorService.getInvoices({
        page,
        limit: pagination.limit,
        ...filterParams
      });
      const payload = response.data || response;
      const list = payload.invoices || (payload.data && payload.data.invoices) || [];
      const meta = payload.pagination || (payload.data && payload.data.pagination) || {
        total: list.length,
        page: page,
        limit: pagination.limit,
        totalPages: 1
      };
      setInvoices(list.map(inv => ({
        ...inv,
        requestedAmount: inv.requestedLoanAmount || inv.totalAmount,
        leadStatus: inv.loanStatus ? inv.loanStatus.toLowerCase() : 'new',
        invoiceNumber: inv.invoiceNumber || inv.invoiceId,
        supplierName: inv.supplierName || 'N/A',
        mobile: inv.contactMobile,
        remarks: inv.remarks || inv.approvalRemarks || inv.rejectionReason || '',
        approvalRemarks: inv.approvalRemarks,
        rejectionReason: inv.rejectionReason,
        amount: inv.totalAmount,
        // Convert string "null" to actual null, and handle empty/null values
        buyerDecision: (inv.buyerDecision === "null" || !inv.buyerDecision) ? null : inv.buyerDecision
      })));
      setPagination({
        currentPage: meta.page,
        limit: meta.limit,
        total: meta.total,
        totalPages: meta.totalPages,
        from: (meta.page - 1) * meta.limit + 1,
        to: Math.min(meta.page * meta.limit, meta.total)
      });
    } catch (error) {
      console.error('Error loading invoices:', error);
      showToast('Failed to load invoices', 'error');
    } finally {
      setLoading(prev => ({ ...prev, invoices: false }));
    }
  };

  const loadSuppliers = async () => {
    setLoading({ ...loading, suppliers: true });
    try {
      // const data = await anchorService.getSuppliers(anchorId);
      
      // Mock data
      const mockSuppliers = [
        {
          id: 1,
          name: 'ABC Suppliers Limited',
          gst: '24ABCDE1234F1Z5',
          status: 'Active',
          totalInvoices: 45,
          approvedInvoices: 42,
          avgAmount: 320000,
          totalFinanced: 12500000,
          contact: { name: 'Rajesh Kumar', phone: '+91-9876543210' }
        },
        {
          id: 2,
          name: 'XYZ Industries',
          gst: '27XYZAB1234C1Z5',
          status: 'Active',
          totalInvoices: 28,
          approvedInvoices: 25,
          avgAmount: 580000,
          totalFinanced: 8200000,
          contact: { name: 'Priya Sharma', phone: '+91-9876543211' }
        },
        {
          id: 3,
          name: 'DEF Enterprises',
          gst: '29DEFGH5678I9J0',
          status: 'Active',
          totalInvoices: 32,
          approvedInvoices: 30,
          avgAmount: 195000,
          totalFinanced: 5900000,
          contact: { name: 'Amit Patel', phone: '+91-9876543212' }
        }
      ];

      setSuppliers(mockSuppliers);
    } catch (error) {
      console.error('Error loading suppliers:', error);
      showToast('Failed to load suppliers', 'error');
    } finally {
      setLoading({ ...loading, suppliers: false });
    }
  };

  const loadAnalytics = async () => {
    setLoading({ ...loading, analytics: true });
    try {
      // const data = await anchorService.getAnalytics(anchorId);
      setAnalyticsData({}); // Will use default data in component
    } catch (error) {
      console.error('Error loading analytics:', error);
      showToast('Failed to load analytics', 'error');
    } finally {
      setLoading({ ...loading, analytics: false });
    }
  };

  const loadProfile = async () => {
    setLoading({ ...loading, profile: true });
    try {
      // const data = await anchorService.getAnchorProfile(anchorId);
      
      // Mock data
      const mockProfile = {
        companyName: 'Your Company Limited',
        gstNumber: '27XYZAB1234C1Z5',
        panNumber: 'XYZAB1234C',
        address: '123 Business District, Mumbai - 400001, Maharashtra, India',
        contactName: 'John Doe',
        contactEmail: 'john.doe@yourcompany.com',
        contactPhone: '+91-9876543210',
        creditLimit: 50000000,
        availableCredit: 35000000
      };

      setAnchorProfile(mockProfile);
    } catch (error) {
      console.error('Error loading profile:', error);
      showToast('Failed to load profile', 'error');
    } finally {
      setLoading({ ...loading, profile: false });
    }
  };

  // Event Handlers
  const handleFilterChange = (filterParams) => {
    setFilters(filterParams);
    loadInvoices(1, filterParams);
  };

  const handlePageChange = (page) => {
    loadInvoices(page, filters);
  };

  const handleLimitChange = (limit) => {
    setPagination({ ...pagination, limit });
    loadInvoices(1, filters);
  };

  const handleViewInvoiceDetails = async (invoiceId) => {
    setSelectedInvoiceId(invoiceId);
    setShowInvoiceModal(true);
    setLoadingInvoiceDetail(true);
    try {
      const response = await anchorService.getInvoiceDetails(invoiceId);
      const payload = response.data || response;
      const data = payload.invoice || payload.data || payload;
      setSelectedInvoiceDetail(data);
    } catch (error) {
      console.error('Error fetching invoice detail:', error);
      showToast('Unable to load invoice detail', 'error');
    } finally {
      setLoadingInvoiceDetail(false);
    }
  };

  const handleViewNotes = (invoice) => {
    // Collect all remarks from invoice (approvalRemarks, rejectionReason, and any other notes)
    const allRemarks = [];
    if (invoice.approvalRemarks) {
      allRemarks.push(`[Approval] ${invoice.approvalRemarks}`);
    }
    if (invoice.rejectionReason) {
      allRemarks.push(`[Rejection] ${invoice.rejectionReason}`);
    }
    if (invoice.remarks) {
      allRemarks.push(invoice.remarks);
    }
    
    const combinedRemarks = allRemarks.join('\n');
    setRemarksToDisplay(combinedRemarks);
    setCurrentInvoiceForRemarks(invoice);
    setNewRemarkText('');
    setShowRemarksModal(true);
  };

  const handleRemarksModalSave = async () => {
    if (!newRemarkText.trim()) {
      showToast('Note cannot be empty', 'error');
      return;
    }

    if (!currentInvoiceForRemarks) return;

    const invoiceId = currentInvoiceForRemarks.invoiceId || currentInvoiceForRemarks.id;
    const timestamp = new Date().toLocaleString();
    const newNoteEntry = `[${timestamp}] ${newRemarkText.trim()}`;
    
    // Combine with existing remarks
    const updatedRemarks = remarksToDisplay 
      ? `${newNoteEntry}\n${remarksToDisplay}` 
      : newNoteEntry;

    try {
      // Call API to save remarks using new unified decision endpoint
      await anchorService.updateInvoiceRemarks(invoiceId, {
        remarks: updatedRemarks
      });

      // Update local state after successful API call
      setInvoices(prev => prev.map(inv => 
        inv.invoiceId === invoiceId 
          ? { ...inv, remarks: updatedRemarks }
          : inv
      ));

      // Update displayed remarks
      setRemarksToDisplay(updatedRemarks);
      setNewRemarkText('');
      setCurrentInvoiceForRemarks(prev => ({ ...prev, remarks: updatedRemarks }));
      
      showToast('Note added successfully', 'success');
      if (addNotification) {
        addNotification(`Note added to invoice #${currentInvoiceForRemarks.invoiceNumber}`, 'info');
      }
    } catch (error) {
      console.error('Error saving note:', error);
      showToast(error.message || 'Failed to save note', 'error');
    }
  };

  const handleAcceptInvoice = (invoice) => {
    setInvoiceToAccept(invoice);
    setShowAcceptModal(true);
  };

  const handleConfirmAccept = async () => {
    if (!invoiceToAccept) return;
    
    const invoiceId = invoiceToAccept.invoiceId || invoiceToAccept.id;
    const approvedAmount = invoiceToAccept.requestedAmount || invoiceToAccept.amount || invoiceToAccept.totalAmount || 0;
    if (!approvedAmount) {
      showToast('Cannot determine approved amount', 'error');
      return;
    }
    try {
      // Add timestamp to remarks
      const timestamp = new Date().toLocaleString('en-IN', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit',
        hour12: false
      });
      const remarksWithTimestamp = `[${timestamp}] Accepted via Anchor dashboard`;
      
      await anchorService.approveInvoice(invoiceId, {
        approvedAmount,
        remarks: remarksWithTimestamp
      });
      
      // Update local state with new remarks
      setInvoices(prev =>
        prev.map(inv =>
          inv.invoiceId === invoiceToAccept.invoiceId 
            ? { 
                ...inv, 
                buyerDecision: 'Accepted', 
                status: 'In Progress',
                remarks: remarksWithTimestamp,
                approvalRemarks: remarksWithTimestamp
              } 
            : inv
        )
      );
      showToast('Invoice accepted', 'success');
      if (addNotification) addNotification('Invoice accepted', 'success');
      setShowAcceptModal(false);
      setInvoiceToAccept(null);
    } catch (error) {
      console.error('Error accepting invoice:', error);
      showToast(error.message || 'Failed to accept invoice', 'error');
    }
  };

  const handleCloseAcceptModal = () => {
    setShowAcceptModal(false);
    setInvoiceToAccept(null);
  };

  const handleRejectInvoiceRow = (invoice) => {
    setInvoiceToReject(invoice);
    setRejectionData({ reason: '', customReason: '' });
    setShowRejectModal(true);
  };

  const handleConfirmRejection = async () => {
    if (!rejectionData.reason) {
      showToast('Please select a rejection reason', 'error');
      return;
    }
    if (rejectionData.reason === 'other' && !rejectionData.customReason.trim()) {
      showToast('Please provide a custom rejection reason', 'error');
      return;
    }

    const invoiceId = invoiceToReject.invoiceId || invoiceToReject.id;
    
    // Map predefined reasons to readable text
    const reasonTextMap = {
      'invoice_details_mismatch': 'Invoice details mismatch with purchase order',
      'goods_not_received': 'Goods/Services not received',
      'quality_issues': 'Quality issues with goods/services',
      'pricing_discrepancy': 'Pricing discrepancy',
      'duplicate_invoice': 'Duplicate invoice',
      'incorrect_gst': 'Incorrect GST details',
      'missing_documents': 'Missing supporting documents',
      'payment_terms_not_met': 'Payment terms not met',
      'supplier_credit_limit_exceeded': 'Supplier credit limit exceeded'
    };
    
    const rejectionReason = rejectionData.reason === 'other' 
      ? rejectionData.customReason.trim()
      : (reasonTextMap[rejectionData.reason] || rejectionData.reason);

    try {
      // Add timestamp to rejection reason
      const timestamp = new Date().toLocaleString('en-IN', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit',
        hour12: false
      });
      const rejectionReasonWithTimestamp = `[${timestamp}] ${rejectionReason}`;
      
      await anchorService.rejectInvoice(invoiceId, {
        rejectionReason: rejectionReasonWithTimestamp
      });
      
      // Update local state with new rejection reason
      setInvoices(prev =>
        prev.map(inv =>
          inv.invoiceId === invoiceId 
            ? { 
                ...inv, 
                buyerDecision: 'Rejected', 
                status: 'Rejected',
                remarks: rejectionReasonWithTimestamp,
                rejectionReason: rejectionReasonWithTimestamp
              } 
            : inv
        )
      );
      showToast('Invoice rejected', 'info');
      if (addNotification) addNotification('Invoice rejected', 'info');
      setShowRejectModal(false);
      setInvoiceToReject(null);
      setRejectionData({ reason: '', customReason: '' });
    } catch (error) {
      console.error('Error rejecting invoice:', error);
      showToast(error.message || 'Failed to reject invoice', 'error');
    }
  };

  const handleCloseRejectModal = () => {
    setShowRejectModal(false);
    setInvoiceToReject(null);
    setRejectionData({ reason: '', customReason: '' });
  };

  const handleCloseInvoiceModal = () => {
    setShowInvoiceModal(false);
    setSelectedInvoiceId(null);
  };

  const handleApproveInvoice = async (approvalData) => {
    try {
      await anchorService.approveInvoice(approvalData.invoiceId, approvalData);
      showToast('Invoice approved successfully!', 'success');
      handleCloseInvoiceModal();
      loadInvoices();
      loadMetrics();
      
      if (addNotification) {
        addNotification('Invoice approved successfully', 'success');
      }
    } catch (error) {
      console.error('Error approving invoice:', error);
      showToast('Failed to approve invoice', 'error');
    }
  };

  const handleRejectInvoice = async (rejectionData) => {
    try {
      await anchorService.rejectInvoice(rejectionData.invoiceId, rejectionData);
      showToast('Invoice rejected', 'info');
      handleCloseInvoiceModal();
      loadInvoices();
      loadMetrics();
      
      if (addNotification) {
        addNotification('Invoice rejected', 'info');
      }
    } catch (error) {
      console.error('Error rejecting invoice:', error);
      showToast('Failed to reject invoice', 'error');
    }
  };

  const handleViewSupplierInvoices = (supplierId) => {
    // Filter invoices by supplier
    const supplierFilter = { supplierId };
    setFilters(supplierFilter);
    // Navigate to invoices tab with filters would be better, but for now we can set state
    // But better is to change tab:
    navigate('/anchor/dashboard?tab=invoices');
    loadInvoices(1, supplierFilter);
  };

  const handleUpdateProfile = async (profileData) => {
    try {
      await anchorService.updateAnchorProfile(anchorId, profileData);
      showToast('Profile updated successfully!', 'success');
      setAnchorProfile(profileData);
    } catch (error) {
      console.error('Error updating profile:', error);
      showToast('Failed to update profile', 'error');
    }
  };

  return (
    <div id="wrapper">
      {/* Header is handled by App.js/Header.js */}
      
      <div className="normalheader">
        <div className="hpanel">
          <div className="panel-body">
            <h2 className="font-light m-b-xs">
              Anchor Dashboard
            </h2>
            <p style={{ color: '#7c7c7c', fontSize: '14px' }}>
              Welcome back, {user?.name || anchorProfile.companyName || 'Anchor Company'} - Manage your supplier invoices and financing
            </p>
          </div>
        </div>
      </div>

      <div className="content">
        {/* Dashboard Content */}
        <div className="dashboard-content">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div>
              <DashboardMetrics 
                metrics={dashboardMetrics} 
                loading={loading.metrics} 
              />
              
              <div style={{ marginTop: '25px' }}>
                <h4 style={{ marginBottom: '20px', fontWeight: '600' }}>
                  <i className="fa fa-clock-o"></i> Recent Invoices
                </h4>
                <InvoiceList
                  invoices={invoices.slice(0, 5)}
                  loading={loading.invoices}
                  onViewDetails={handleViewInvoiceDetails}
                  onViewNotes={handleViewNotes}
                  onAccept={handleAcceptInvoice}
                  onReject={handleRejectInvoiceRow}
                  pagination={null}
                />
                <div style={{ textAlign: 'center', marginTop: '15px' }}>
                  <button 
                    className="btn btn-primary"
                    onClick={() => navigate('/anchor/dashboard?tab=invoices')}
                  >
                    View All Invoices <i className="fa fa-arrow-right"></i>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div>
            <InvoiceFilters
              onFilterChange={handleFilterChange}
            />
            <div className="hpanel panel panel-default">
              <div className="panel-heading" style={{ background: '#fff', padding: '15px 20px', borderBottom: '1px solid #e7eaec', borderRadius: '8px 8px 0 0' }}>
                <h5 style={{ margin: 0, fontWeight: '600' }}>Invoices</h5>
              </div>
              <div className="panel-body" style={{ background: '#fff', borderRadius: '0 0 8px 8px', padding: '10px 20px 20px' }}>
                <InvoiceList
                  invoices={invoices}
                  loading={loading.invoices}
                  onViewDetails={handleViewInvoiceDetails}
                  onViewNotes={handleViewNotes}
                  onAccept={handleAcceptInvoice}
                  onReject={handleRejectInvoiceRow}
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  onLimitChange={handleLimitChange}
                />
              </div>
            </div>
          </div>
        )}

          {/* Suppliers Tab */}
          {activeTab === 'suppliers' && (
            <SupplierManagement
              suppliers={suppliers}
              loading={loading.suppliers}
              onViewSupplierInvoices={handleViewSupplierInvoices}
            />
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <AnalyticsCharts
              analyticsData={analyticsData}
              loading={loading.analytics}
            />
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <AnchorSettings
              anchorProfile={anchorProfile}
              onUpdateProfile={handleUpdateProfile}
              loading={loading.profile}
            />
          )}
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Invoice Detail Modal */}
      {showInvoiceModal && (
        <InvoiceDetailModal
          invoiceId={selectedInvoiceId}
          onClose={handleCloseInvoiceModal}
          onApprove={handleApproveInvoice}
          onReject={handleRejectInvoice}
          loading={loadingInvoiceDetail}
          invoice={selectedInvoiceDetail}
        />
      )}

      {/* Rejection Modal */}
      {showRejectModal && invoiceToReject && (
        <div className="modal-backdrop show" style={{ zIndex: 1050 }}>
          <div className="modal show" style={{ display: 'block', zIndex: 1051 }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">
                    <i className="fa fa-times-circle" style={{ color: '#F44336' }}></i> Reject Invoice
                  </h4>
                  <button 
                    type="button" 
                    className="close" 
                    onClick={handleCloseRejectModal}
                    style={{ fontSize: '28px' }}
                  >
                    &times;
                  </button>
                </div>
                <div className="modal-body">
                  <p style={{ marginBottom: '20px' }}>
                    <strong>Invoice No:</strong> {invoiceToReject.invoiceNumber || invoiceToReject.invoiceId}
                    <br />
                    <strong>Supplier:</strong> {invoiceToReject.supplierName || 'N/A'}
                    <br />
                    <strong>Amount:</strong> ₹{invoiceToReject.totalAmount?.toLocaleString('en-IN') || '0'}
                  </p>
                  
                  <div className="form-group">
                    <label>
                      Reason for Rejection <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-control"
                      value={rejectionData.reason}
                      onChange={(e) => setRejectionData({ ...rejectionData, reason: e.target.value, customReason: '' })}
                      style={{ marginBottom: '10px' }}
                    >
                      <option value="">-- Select Reason --</option>
                      <option value="invoice_details_mismatch">Invoice details mismatch with purchase order</option>
                      <option value="goods_not_received">Goods/Services not received</option>
                      <option value="quality_issues">Quality issues with goods/services</option>
                      <option value="pricing_discrepancy">Pricing discrepancy</option>
                      <option value="duplicate_invoice">Duplicate invoice</option>
                      <option value="incorrect_gst">Incorrect GST details</option>
                      <option value="missing_documents">Missing supporting documents</option>
                      <option value="payment_terms_not_met">Payment terms not met</option>
                      <option value="supplier_credit_limit_exceeded">Supplier credit limit exceeded</option>
                      <option value="other">Other (Please specify)</option>
                    </select>
                  </div>

                  {rejectionData.reason === 'other' && (
                    <div className="form-group">
                      <label>
                        Custom Reason <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className="form-control"
                        rows="4"
                        placeholder="Please provide detailed reason for rejection..."
                        value={rejectionData.customReason}
                        onChange={(e) => setRejectionData({ ...rejectionData, customReason: e.target.value })}
                        style={{ minHeight: '100px' }}
                      />
                      <small className="text-muted">
                        {rejectionData.customReason.length} characters (minimum 10 required)
                      </small>
                    </div>
                  )}

                  <div className="alert alert-warning" style={{ marginTop: '15px', marginBottom: '0' }}>
                    <i className="fa fa-exclamation-triangle"></i> This action cannot be undone. The invoice will be marked as rejected.
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-default"
                    onClick={handleCloseRejectModal}
                  >
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-danger"
                    onClick={handleConfirmRejection}
                    disabled={!rejectionData.reason || (rejectionData.reason === 'other' && rejectionData.customReason.trim().length < 10)}
                  >
                    <i className="fa fa-times"></i> Confirm Rejection
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Accept Modal */}
      {showAcceptModal && invoiceToAccept && (
        <div className="modal-backdrop show" style={{ zIndex: 1050 }}>
          <div className="modal show" style={{ display: 'block', zIndex: 1051 }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">
                    <i className="fa fa-check-circle" style={{ color: '#4CAF50' }}></i> Approve Invoice
                  </h4>
                  <button 
                    type="button" 
                    className="close" 
                    onClick={handleCloseAcceptModal}
                    style={{ fontSize: '28px' }}
                  >
                    &times;
                  </button>
                </div>
                <div className="modal-body">
                  <p style={{ marginBottom: '20px' }}>
                    <strong>Invoice No:</strong> {invoiceToAccept.invoiceNumber || invoiceToAccept.invoiceId}
                    <br />
                    <strong>Supplier:</strong> {invoiceToAccept.supplierName || 'N/A'}
                    <br />
                    <strong>Amount:</strong> ₹{invoiceToAccept.totalAmount?.toLocaleString('en-IN') || '0'}
                  </p>
                  
                  <div className="alert alert-info" style={{ 
                    marginTop: '15px', 
                    marginBottom: '0',
                    backgroundColor: '#E8F5E9',
                    borderColor: '#4CAF50',
                    color: '#2E7D32'
                  }}>
                    <i className="fa fa-info-circle" style={{ color: '#4CAF50' }}></i> The invoice will be approved and marked as accepted.
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-default"
                    onClick={handleCloseAcceptModal}
                  >
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-success"
                    onClick={handleConfirmAccept}
                  >
                    <i className="fa fa-check"></i> Confirm Approval
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remarks Modal - View & Add Notes */}
      {showRemarksModal && currentInvoiceForRemarks && (
        <div className="modal" style={{ 
          display: 'flex', 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(0,0,0,0.5)',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1050
        }}>
          <div className="modal-dialog" style={{ maxWidth: '700px', width: '90%', margin: 0 }}>
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <h5 className="modal-title" style={{ 
                  color: '#495057', 
                  fontWeight: '600',
                  fontSize: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <i className="fa fa-comments-o" style={{ color: '#007bff', fontSize: '20px' }}></i>
                  <span>Notes & Remarks</span>
                  <span style={{ 
                    fontSize: '14px', 
                    fontWeight: '500',
                    color: '#6c757d',
                    backgroundColor: '#e9ecef',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontFamily: 'monospace'
                  }}>
                    Invoice #{currentInvoiceForRemarks.invoiceNumber || currentInvoiceForRemarks.invoiceId}
                  </span>
                </h5>
                <button 
                  type="button" 
                  className="close" 
                  onClick={() => {
                    setShowRemarksModal(false);
                    setNewRemarkText('');
                    setCurrentInvoiceForRemarks(null);
                  }}
                  style={{
                    fontSize: '28px',
                    fontWeight: 'bold',
                    color: '#6c757d',
                    opacity: '0.8'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.opacity = '1';
                    e.target.style.color = '#dc3545';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.opacity = '0.8';
                    e.target.style.color = '#6c757d';
                  }}
                >
                  &times;
                </button>
              </div>
              <div className="modal-body">
                {/* Existing Notes Section */}
                <div className="mb-3">
                  <h6 className="text-muted mb-2">
                    <i className="fa fa-history"></i>
                    Previous Notes ({remarksToDisplay ? remarksToDisplay.split('\n').length : 0})
                  </h6>
                  <div 
                    style={{ 
                      maxHeight: '200px', 
                      overflowY: 'auto', 
                      border: '1px solid #ddd', 
                      borderRadius: '4px',
                      padding: '10px',
                      backgroundColor: '#f9f9f9'
                    }}
                  >
                    {remarksToDisplay ? (
                      remarksToDisplay.split('\n').map((note, idx) => (
                        <div 
                          key={idx} 
                          style={{ 
                            marginBottom: '8px', 
                            paddingBottom: '8px', 
                            borderBottom: idx === remarksToDisplay.split('\n').length - 1 ? 'none' : '1px solid #eee',
                            whiteSpace: 'pre-wrap',
                            fontSize: '14px',
                            lineHeight: '1.4'
                          }}
                        >
                          {note}
                        </div>
                      ))
                    ) : (
                      <p className="text-muted mb-0">No previous notes available.</p>
                    )}
                  </div>
                </div>

                {/* Add New Note Section */}
                <div className="mt-4">
                  <h6 className="text-primary mb-2">
                    <i className="fa fa-plus-circle"></i>
                    Add New Note
                  </h6>
                  <textarea
                    className="form-control"
                    rows="4"
                    placeholder="Enter your new note/remark here..."
                    value={newRemarkText}
                    onChange={(e) => setNewRemarkText(e.target.value)}
                    style={{
                      border: '2px solid #e9ecef',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  ></textarea>
                  <small className="text-muted">
                    <i className="fa fa-info-circle"></i>
                    Your note will be timestamped and added to the history above.
                  </small>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: '2px solid #e9ecef' }}>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowRemarksModal(false);
                    setNewRemarkText('');
                    setCurrentInvoiceForRemarks(null);
                  }}
                >
                  <i className="fa fa-times"></i>
                  Close
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={handleRemarksModalSave}
                  disabled={!newRemarkText.trim()}
                  style={{
                    opacity: newRemarkText.trim() ? 1 : 0.6
                  }}
                >
                  <i className="fa fa-save"></i>
                  Save Note
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnchorDashboard;
